import React from 'react'

import { faCalendar, faCopy, faFile } from '@fortawesome/pro-regular-svg-icons'
import {
  faCalendar as faSolidCalendar,
  faCopy as faSolidCopy,
  faFile as faSolidFile,
} from '@fortawesome/pro-solid-svg-icons'
import { Button, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Field, Form, Formik } from 'formik'
import ReCAPTCHA from 'react-google-recaptcha'
import * as Yup from 'yup'

import CollegeAutocomplete, { CollegeOption } from 'components/CollegeAutocomplete'
import ReturnToLogin from 'components/ReturnToLogin'
import SelectableChip, { Option } from 'components/SelectableChip'
import TermsAndPrivacy from 'components/TermsAndPrivacy'
import Title from 'components/Title'

const useStyles = makeStyles((theme) => ({
  container: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chipGroup: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  centerAlign: {
    display: 'flex',
    justifyContent: 'center',
  },
}))

const ACCESS_TYPES = [
  {
    title: 'Docs',
    value: 'docs_only',
    faIconUnselected: faFile,
    faIconSelected: faSolidFile,
    subtitle: 'Receive transcripts & application docs',
  },
  {
    title: 'Visits+',
    value: 'visits_only',
    faIconUnselected: faCalendar,
    faIconSelected: faSolidCalendar,
    subtitle: 'Schedule and manage high school visits',
  },
  {
    title: 'Both',
    value: 'both',
    faIconUnselected: faCopy,
    faIconSelected: faSolidCopy,
    subtitle: 'Doc delivery & visit scheduling',
  },
]
export type AccessOption = 'docs_only' | 'visits_only' | 'both'

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Please enter a valid email.').required('Email is required.'),
  selectedCollege: Yup.mixed<CollegeOption>().required('Please select a college.'),
  accessType: Yup.mixed<AccessOption>().required('Please select an access type.'),
  recaptchaResponse: Yup.string().required('Please complete the reCAPTCHA.'),
})

type RequestAOAccessValues = {
  email: string
  selectedCollege: CollegeOption | null
  accessType: AccessOption | ''
  recaptchaResponse: string | ''
}

type RequestAOAccessProps = {
  onSubmit: (values: {
    email: string
    selectedCollege: CollegeOption
    accessType: AccessOption
    recaptchaResponse: string
  }) => void
}
const RequestAOAccess = ({ onSubmit }: RequestAOAccessProps) => {
  const classes = useStyles()
  const initialValues: RequestAOAccessValues = {
    email: '',
    selectedCollege: null,
    accessType: '',
    recaptchaResponse: '',
  }
  const recaptchaRef = React.createRef<ReCAPTCHA>()

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={(values) => onSubmit(values as Yup.InferType<typeof validationSchema>)}
    >
      {({ errors, touched, isValid, isSubmitting, setFieldValue, values }) => {
        return (
          <>
            <Title title="Sign up to access basic solutions" />
            <Form>
              <div className={classes.container}>
                <Typography variant="subtitle2">What type of access are you looking for?</Typography>
                <div className={classes.chipGroup}>
                  <SelectableChip
                    options={ACCESS_TYPES}
                    onSelect={(value: Option) => setFieldValue('accessType', value)}
                    selectedItem={values.accessType}
                  />
                </div>

                <Field
                  as={CollegeAutocomplete}
                  onSelect={(college: CollegeOption | null) => setFieldValue('selectedCollege', college)}
                  name="selectedCollege"
                  error={touched.selectedCollege && Boolean(errors.selectedCollege)}
                  helperText={touched.selectedCollege && errors.selectedCollege}
                />
                <Field
                  as={TextField}
                  variant="outlined"
                  label="Your Institutional Email Address"
                  name="email"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
                <ReCAPTCHA
                  className={classes.centerAlign}
                  ref={recaptchaRef}
                  sitekey={window.RECAPTCHA_SITE_KEY}
                  onChange={() => {
                    setFieldValue('recaptchaResponse', recaptchaRef.current?.getValue(), true)
                  }}
                />
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={!isValid || isSubmitting || values.recaptchaResponse === ''}
                >
                  Continue
                </Button>
              </div>
            </Form>
            <TermsAndPrivacy altText="By continuing" />
            <ReturnToLogin />
          </>
        )
      }}
    </Formik>
  )
}

export default RequestAOAccess
